import React, {useEffect, createContext, useContext, useRef} from 'react'
import PropTypes from 'prop-types'
import Section from '../../../../components/section'
import {Box} from '@chakra-ui/react3'

// Create context to track if we're inside a Container and animation state
const StackContext = createContext({isInContainer: false, noAnimation: true})

function Container({children, noAnimation = false}) {
    return (
        <StackContext.Provider value={{isInContainer: true, noAnimation}}>
            <Section position="relative" paddingBottom="0">
                {React.Children.map(children, (child, index) => {
                    // Check if child is a valid React element and of type Item
                    if (!React.isValidElement(child) || child.type !== Item) {
                        throw new Error(
                            'Stack.Container only accepts Stack.Item as children. ' +
                                `Received: ${child?.type?.name || typeof child}. ` +
                                'Please use Stack.Item components as direct children of Stack.Container.'
                        )
                    }
                    return React.cloneElement(child, {index})
                })}
            </Section>
        </StackContext.Provider>
    )
}

Container.propTypes = {
    children: PropTypes.node.isRequired,
    noAnimation: PropTypes.bool
}

function Item({children, index}) {
    const {isInContainer, noAnimation} = useContext(StackContext)
    const highlightRef = useRef(null)

    if (!isInContainer) {
        throw new Error(
            'Stack.Item must be used as a child of Stack.Container. ' +
                'Please wrap your Stack.Item components with Stack.Container.'
        )
    }

    useEffect(() => {
        if (!highlightRef.current || noAnimation) return

        const highlightElement = highlightRef.current

        function updateTopProperty() {
            const highlightRect = highlightElement.getBoundingClientRect()
            const stickyTopOffset = Math.min(window.innerHeight - highlightRect.height, 0)
            highlightElement.style.top = `${stickyTopOffset}px`
        }

        // resize observer to react properly to container changes, this is useful whenever our
        // content has element like images which can break the top value for the sticky behavior
        const resizeObserver = new ResizeObserver(() => {
            updateTopProperty()
        })

        resizeObserver.observe(highlightElement)

        return () => {
            resizeObserver.disconnect()
        }
    }, [noAnimation])

    useEffect(() => {
        const highlighElement = highlightRef.current

        const focusInHandler = (e) => {
            // only scroll to current item if focus in was using Tab
            if (e.target.matches(':focus-visible')) {
                const containerRect = highlighElement.getBoundingClientRect()
                const highlighHeight = containerRect.height
                const highlightsContainer = highlighElement.parentElement

                window.scrollTo({
                    top: highlightsContainer.offsetTop + index * highlighHeight
                })
            }
        }

        highlighElement.addEventListener('focusin', focusInHandler)

        return () => highlighElement.removeEventListener('focusin', focusInHandler)
    }, [index])

    return (
        <Box position={noAnimation ? 'static' : 'sticky'} ref={highlightRef}>
            {children}
        </Box>
    )
}

Item.propTypes = {
    children: PropTypes.node.isRequired,
    index: PropTypes.number
}

const Stack = {
    Container,
    Item
}

export default Stack
